import { cn } from "@/lib/utils";
import type { ParcelInfo } from "../types/deforestation";

interface ForestCoverTrendProps {
  parcels: ParcelInfo[];
}

export function ForestCoverTrend({ parcels }: ForestCoverTrendProps) {
  const total2020M2 = parcels.reduce(
    (s, p) => s + (p.stats?.treeArea2020M2 ?? 0),
    0,
  );
  const total2025M2 = parcels.reduce(
    (s, p) => s + (p.stats?.treeArea2025M2 ?? 0),
    0,
  );
  const total2020Ha = total2020M2 / 10_000;
  const total2025Ha = total2025M2 / 10_000;
  const changeHa = total2025Ha - total2020Ha;
  const changePct = total2020Ha > 0 ? (changeHa / total2020Ha) * 100 : 0;
  const isLoss = changeHa < 0;

  const max = Math.max(total2020Ha, total2025Ha, 1);
  const baselinePct = (total2020Ha / max) * 100;
  const currentPct = (total2025Ha / max) * 100;

  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <h3 className="mb-3 text-sm font-medium text-foreground">
        Forest Cover Change
      </h3>
      <div className="space-y-3">
        <Bar
          label="2020 (Baseline)"
          valueHa={total2020Ha}
          widthPct={baselinePct}
          colorClass="bg-green-600 dark:bg-green-500"
        />
        <Bar
          label="2025 (Current)"
          valueHa={total2025Ha}
          widthPct={currentPct}
          colorClass="bg-amber-500 dark:bg-amber-400"
        />
        <div className="flex items-center justify-between rounded-md bg-muted p-2 text-xs">
          <span className="text-muted-foreground">
            {isLoss ? "Net forest loss" : "Net forest gain"}
          </span>
          <span
            className={cn(
              "font-semibold",
              isLoss ? "text-red-500" : "text-green-500",
            )}
          >
            {Math.abs(changeHa).toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })}{" "}
            Ha ({Math.abs(changePct).toFixed(1)}%)
          </span>
        </div>
      </div>
    </div>
  );
}

interface BarProps {
  label: string;
  valueHa: number;
  widthPct: number;
  colorClass: string;
}

function Bar({ label, valueHa, widthPct, colorClass }: BarProps) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">
          {valueHa.toLocaleString(undefined, { maximumFractionDigits: 1 })} Ha
        </span>
      </div>
      <div className="h-4 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", colorClass)}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}
